import {api, RequestParam} from "@/shared/api";
import * as AttachmentUtil from "@/shared/attachments/attachmentUtil";
import {statusElementsHolder} from "@/shared/recording/statusElementsHolder";
import {FormUtil} from "@/shared/forms/formUtil";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {
	AbstractWorkingTimeRequestResponseModel,
	AttachmentUploadUrlResponse,
	ErrorResponse,
	WorkingTimeDateSpanRequestResponseModel,
	WorkingTimeNoticeRequestResponseModel,
	WorkingTimeRequestResponseModel,
	WorkingTimeType
} from "@/shared/timr";
import {
	ProofReviewAction,
	ProofStatus,
	WorkingTimeRequestEvent,
	WorkingTimeRequestStatus,
	WorkingTimeTypeRecordingMode
} from "@/shared/timrTypes";
import {ProofAttachmentGallery} from "@/shared/workingtime/proofAttachmentGallery";
import {formatDateRange, formatDateTimeRange, parseISODate, parseISOZonedDateTime} from "@/shared/utils/datefunctions";

export interface SaveWorkingTimeRequestResponse {
	workingTimeRequest: AbstractWorkingTimeRequestResponseModel;
	nextAvailableDay: string;
	proofUploadUrls?: AttachmentUploadUrlResponse[];
}

export class WorkingTimeRequestUtil {
	public static readonly SEND_WORKING_TIME_REQUEST_EVENT_URL: string = "requests/sendWorkingTimeRequestEvent.json";

	/**
	 * List of statuses considered as "active" for a working time request.
	 * This is used for calendars
	 */
	public static readonly ACTIVE_STATUSES = [
		WorkingTimeRequestStatus.ApprovalRequested,
		WorkingTimeRequestStatus.InApproval,
		WorkingTimeRequestStatus.Approved,
		WorkingTimeRequestStatus.CancellationRequested,
		WorkingTimeRequestStatus.InCancellation
	];

	/**
	 * List of statuses considered as "unfinished" and in an active approval process for a working time request.
	 * Used to identify requests that are not yet completed.
	 */
	public static readonly UNFINISHED_STATUSES = [
		WorkingTimeRequestStatus.ApprovalRequested,
		WorkingTimeRequestStatus.InApproval,
		WorkingTimeRequestStatus.CancellationRequested,
		WorkingTimeRequestStatus.InCancellation
	];

	public static isImmediateStatusChangeAllowed(workingTimeRequest: AbstractWorkingTimeRequestResponseModel, isGroupLeaderForThemself: boolean): boolean {
		const wtrId = workingTimeRequest.user.id;
		return globals.isAuthorizedForAllUsers || globals.isWorkingTimeRequestsOverrideAllowed && globals.isGroupLeader && (wtrId !== globals.userId || wtrId === globals.userId && isGroupLeaderForThemself);
	}

	public static handleConfirmPromptFail(response: ErrorResponse, modal: BootstrapModal.Modal) {
		modal.resetLoadingButtons();
		if (response.responseJSON?.fieldErrors) {
			FormUtil.markFieldErrors($("#confirmPrompt"), response.responseJSON.fieldErrors);
			modal.resetLoadingButtons();
		} else {
			FormUtil.removeAllFeedback($("#confirmPrompt"));
		}
	}

	public static toggleWorkingTimeTypesByRequestNotAvailableWarning(workingTimeTypes: WorkingTimeType[]) {
		const workingTimeTypeByRequestAvailable = workingTimeTypes.some((wtt) => !wtt.archived && wtt.recordingModeUser === WorkingTimeTypeRecordingMode.BY_REQUEST);
		$("#workingTimeTypesByRequestNotAvailableWarning").toggle(!workingTimeTypeByRequestAvailable);
	}

	public static isInApprovalProcess(workingTimeRequest: AbstractWorkingTimeRequestResponseModel): boolean {
		return workingTimeRequest.status === WorkingTimeRequestStatus.ApprovalRequested ||
			workingTimeRequest.status === WorkingTimeRequestStatus.InApproval;
	}

	public static isInCancellationProcess(workingTimeRequest: AbstractWorkingTimeRequestResponseModel): boolean {
		return workingTimeRequest.status === WorkingTimeRequestStatus.CancellationRequested ||
			workingTimeRequest.status === WorkingTimeRequestStatus.InCancellation;
	}

	/**
	 * Determines if the proof attachments of a working time request can be changed,
	 * which is the case if the current user is an admin or is a group leader of the wtr user
	 * or if the request is in the approval process and the proof status is not yet decided.
	 * @param workingTimeRequest 		The working time request to check
	 * @param isGroupLeaderForThemself 	Whether the user is group leader for themselves
	 */
	public static isAllowedToChangeProofAttachments(workingTimeRequest: AbstractWorkingTimeRequestResponseModel, isGroupLeaderForThemself: boolean): boolean {
		const isLeaderOfUser = globals.userId === workingTimeRequest.user.id ? isGroupLeaderForThemself : globals.isGroupLeader;
		return globals.isAdmin || isLeaderOfUser ||
			WorkingTimeRequestUtil.isInApprovalProcess(workingTimeRequest) &&
			workingTimeRequest.proofStatus !== ProofStatus.REJECTED &&
			workingTimeRequest.proofStatus !== ProofStatus.ACCEPTED;
	}

	public static approveWorkingTimeRequestImmediately(workingTimeRequest: AbstractWorkingTimeRequestResponseModel, callback: () => void, onClose?: () => void): BootstrapModal.Modal {
		return this.handleWorkingTimeRequestAction(
			"APPROVAL",
			workingTimeRequest,
			callback,
			"workingTimeRequestStatus.approve.title",
			"workingTimeRequestStatus.approve.message",
			WorkingTimeRequestEvent.APPROVE_IMMEDIATELY,
			null,
			null,
			"btn-success",
			onClose
		);
	}

	public static rejectWorkingTimeRequestImmediately(workingTimeRequest: AbstractWorkingTimeRequestResponseModel, callback: () => void, onClose?: () => void): BootstrapModal.Modal {
		return this.handleWorkingTimeRequestAction(
			"APPROVAL",
			workingTimeRequest,
			callback,
			"workingTimeRequestStatus.reject.title",
			"workingTimeRequestStatus.reject.message",
			WorkingTimeRequestEvent.REJECT_IMMEDIATELY,
			null,
			null,
			"btn-danger",
			onClose
		);
	}

	public static approveCancellationWorkingTimeRequestImmediately(workingTimeRequest: AbstractWorkingTimeRequestResponseModel, callback: () => void, onClose?: () => void): BootstrapModal.Modal {
		return this.handleWorkingTimeRequestAction(
			"CANCELLATION",
			workingTimeRequest,
			callback,
			"workingTimeRequestStatus.approveCancellation.title",
			"workingTimeRequestStatus.approveCancellation.message",
			WorkingTimeRequestEvent.APPROVE_CANCELLATION_IMMEDIATELY,
			null,
			null,
			"btn-success",
			onClose
		);
	}

	public static rejectCancellationWorkingTimeRequestImmediately(workingTimeRequest: AbstractWorkingTimeRequestResponseModel, callback: () => void, onClose?: () => void): BootstrapModal.Modal {
		return this.handleWorkingTimeRequestAction(
			"CANCELLATION",
			workingTimeRequest,
			callback,
			"workingTimeRequestStatus.rejectCancellation.title",
			"workingTimeRequestStatus.rejectCancellation.message",
			WorkingTimeRequestEvent.REJECT_CANCELLATION_IMMEDIATELY,
			null,
			null,
			"btn-danger",
			onClose
		);
	}

	public static cancelImmediatelyWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel, callback: () => void, onClose?: () => void): BootstrapModal.Modal {
		return BootstrapModal.confirmPrompt({
			title: "workingTimeRequestStatus.cancelImmediately.title",
			message: messages["workingTimeRequestStatus.cancelImmediately.message"],
			yesLabel: "confirmation.cancelImmediately",
			yesCss: "btn-danger",
			noLabel: "confirmation.cancel",
			noCss: "btn-default",
			promptNameTag: "statusComment",
			css: {
				header: "bg-danger"
			},
			onClose: onClose,
			yesAction: (modal: BootstrapModal.Modal) => {
				api(WorkingTimeRequestUtil.SEND_WORKING_TIME_REQUEST_EVENT_URL)
					.post({
						workingTimeRequestId: workingTimeRequest.id,
						workingTimeRequestEvent: WorkingTimeRequestEvent.CANCEL_IMMEDIATELY,
						comment: modal.getReason()
					})
					.done(() => {
						modal.close();
						statusElementsHolder.updateRequestsApproval();
						callback();
					}).fail((response) => {
					WorkingTimeRequestUtil.handleConfirmPromptFail(response, modal);
				});
			}
		});
	}

	public static requestCancellationWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel, callback: () => void, onClose?: () => void): BootstrapModal.Modal {
		return BootstrapModal.confirmPrompt({
			title: "workingTimeRequestStatus.requestCancellation.title",
			message: messages["workingTimeRequestStatus.requestCancellation.message"],
			yesLabel: "confirmation.requestCancellation",
			yesCss: "btn-warning",
			noLabel: "confirmation.cancel",
			noCss: "btn-default",
			promptNameTag: "statusComment",
			css: {
				header: "bg-warning"
			},
			onClose: onClose,
			yesAction: (modal: BootstrapModal.Modal) => {
				api(WorkingTimeRequestUtil.SEND_WORKING_TIME_REQUEST_EVENT_URL)
					.post({
						workingTimeRequestId: workingTimeRequest.id,
						workingTimeRequestEvent: WorkingTimeRequestEvent.REQUEST_CANCELLATION,
						comment: modal.getReason()
					})
					.done(() => {
						modal.close();
						statusElementsHolder.updateRequestsApproval();
						callback();
					})
					.fail((response) => WorkingTimeRequestUtil.handleConfirmPromptFail(response, modal));
			}
		});
	}

	public static deleteWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel, callback: () => void, onClose?: () => void): BootstrapModal.Modal {
		return BootstrapModal.confirm({
			title: "workingTimeRequestStatus.delete.title",
			message: messages["workingTimeRequestStatus.delete.message"],
			yesLabel: "confirmation.delete",
			noLabel: "confirmation.cancel",
			onClose: onClose,
			yesAction: (modal: BootstrapModal.Modal) => {
				api("requests/deleteWorkingTimeRequest.json")
					.post({
						id: workingTimeRequest.id
					})
					.done(() => {
						statusElementsHolder.updateRequestsApproval();
						callback();
					})
					.always(() => modal.close());
			}
		});
	}

	public static handleWorkingTimeRequestAction(
		type: "APPROVAL" | "CANCELLATION",
		workingTimeRequest: AbstractWorkingTimeRequestResponseModel,
		callback: () => void,
		statusKey: string,
		messageKey: string,
		event: WorkingTimeRequestEvent,
		workingTimeRequestStepId: string,
		proofReviewAction: ProofReviewAction,
		btnCss: string,
		onClose?: () => void
	): BootstrapModal.Modal {
		return BootstrapModal.confirmPrompt({
			title: statusKey,
			message: messages[messageKey],
			yesLabel: type === "APPROVAL"
				? (btnCss.startsWith("btn-success") ? "confirmation.approve" : "confirmation.reject")
				: (btnCss.startsWith("btn-success") ? "confirmation.approveCancellation" : "confirmation.rejectCancellation"),
			yesCss: btnCss,
			noLabel: "confirmation.cancel",
			noCss: "btn-default",
			promptNameTag: "statusComment",
			css: {
				header: btnCss
			},
			onClose: onClose,
			yesAction: (modal: BootstrapModal.Modal) => {
				api(WorkingTimeRequestUtil.SEND_WORKING_TIME_REQUEST_EVENT_URL)
					.post({
						workingTimeRequestId: workingTimeRequest.id,
						workingTimeRequestEvent: event,
						workingTimeRequestStepId: workingTimeRequestStepId,
						comment: modal.getReason(),
						proofReviewAction: proofReviewAction
					})
					.done(() => {
						modal.close();
						statusElementsHolder.updateRequestsApproval();
						callback();
					}).fail((response) => {
					this.handleConfirmPromptFail(response, modal);
				});
			}
		});
	}

	/**
	 * Saves a date-span (whole-/half-day) working time request via recording/saveWorkingTimeDateSpanRequest.json and
	 * uploads any new proof attachments to S3, confirming each upload. The save request includes attachment IDs to
	 * delete via getSerializedArrayElements() so the backend can perform deletions. When there are attachments to
	 * delete, shows a confirmation modal first; on confirm runs save+upload and invokes callbacks.
	 * <p>
	 * The caller picks this or {@link saveWorkingTimeRequestWithProofAttachments} based on the selected working time
	 * type's edit unit, so the request subtype is explicit on the wire.
	 */
	public static saveWorkingTimeDateSpanRequestWithProofAttachments(
		this: void,
		params: RequestParam[],
		proofAttachmentGallery: ProofAttachmentGallery | null | undefined,
		onOpenRequestAfterUploadFailed?: (workingTimeRequest: AbstractWorkingTimeRequestResponseModel) => void
	): Promise<SaveWorkingTimeRequestResponse> {
		return WorkingTimeRequestUtil.saveWithProofAttachments(
			"recording/saveWorkingTimeDateSpanRequest.json",
			params,
			proofAttachmentGallery,
			onOpenRequestAfterUploadFailed
		);
	}

	/**
	 * Saves a minute-based working time request via recording/saveMinuteBasedWorkingTimeRequest.json. Counterpart to
	 * {@link saveWorkingTimeDateSpanRequestWithProofAttachments}; the caller selects it for minute-based working time
	 * types. Proof attachment handling for minute-based requests is not wired yet (TIMR-12539), so the shared
	 * proof-upload flow is a no-op here (the backend returns no upload URLs).
	 */
	public static saveWorkingTimeRequestWithProofAttachments(
		this: void,
		params: RequestParam[],
		proofAttachmentGallery: ProofAttachmentGallery | null | undefined,
		onOpenRequestAfterUploadFailed?: (workingTimeRequest: AbstractWorkingTimeRequestResponseModel) => void
	): Promise<SaveWorkingTimeRequestResponse> {
		return WorkingTimeRequestUtil.saveWithProofAttachments(
			"recording/saveMinuteBasedWorkingTimeRequest.json",
			params,
			proofAttachmentGallery,
			onOpenRequestAfterUploadFailed
		);
	}

	public static saveProofAttachments(
		params: RequestParam[],
		proofAttachmentGallery: ProofAttachmentGallery | null | undefined,
		onOpenRequestAfterUploadFailed?: (workingTimeRequest: AbstractWorkingTimeRequestResponseModel) => void
	): Promise<SaveWorkingTimeRequestResponse> {
		return WorkingTimeRequestUtil.saveWithProofAttachments(
			"recording/saveWorkingTimeRequestProofAttachments.json",
			params,
			proofAttachmentGallery,
			onOpenRequestAfterUploadFailed
		);
	}

	/**
	 * Loads a single working time request fresh from the server by id.
	 */
	public static loadWorkingTimeRequestById(workingTimeRequestId: number): PromiseLike<AbstractWorkingTimeRequestResponseModel> {
		return api("requests/findWorkingTimeRequestById.json").get({workingTimeRequestId});
	}

	/**
	 * @param onOpenRequestAfterUploadFailed Optional callback to reopen the saved request (e.g. its edit dialog). When provided,
	 *                      the proof-upload-failure dialog offers an "Open Request" button that invokes it.
	 */
	private static async saveWithProofAttachments(
		saveUrl: string,
		params: RequestParam[],
		proofAttachmentGallery: ProofAttachmentGallery | null | undefined,
		onOpenRequestAfterUploadFailed?: (workingTimeRequest: AbstractWorkingTimeRequestResponseModel) => void
	): Promise<SaveWorkingTimeRequestResponse> {
		const attachmentsToDelete = proofAttachmentGallery?.getAttachmentsToDelete() ?? [];
		if (attachmentsToDelete.length > 0) {
			const fileNames = attachmentsToDelete.map((attachment) => attachment.metadata.fileName).join(", ");
			await this.confirmAttachmentDeletion(fileNames);
		}

		const fullParams = params.concat(proofAttachmentGallery?.getSerializedArrayElements() ?? []);
		const newFiles = proofAttachmentGallery?.getFilesToUpload() ?? [];

		const data = await api(saveUrl).post(fullParams) as SaveWorkingTimeRequestResponse;
		const requestId = data.workingTimeRequest?.id ?? null;
		const uploadUrls = data.proofUploadUrls ?? [];
		if (requestId && newFiles.length > 0 && uploadUrls.length > 0) {
			const uploadResults = await AttachmentUtil.uploadToS3(newFiles, uploadUrls);

			// Only confirm uploads that S3 actually acknowledged. Confirming a failed upload would mark the
			// attachment as present in the backend while the object is missing in S3 (e.g. an expired presigned
			// URL or a network error), which later surfaces as a broken download.
			const confirmedAttachmentIds = uploadResults.filter((result) => result.success).map((result) => result.attachmentId);
			if (confirmedAttachmentIds.length > 0) {
				// Confirm all successful uploads in a single request. The backend recalculates the shared Proof
				// status once, so there is no need for sequential calls to avoid optimistic locking.
				await api("requests/confirmProofAttachmentUploads.json")
					.options({contentType: "application/json"})
					.post(JSON.stringify({
						workingTimeRequestId: requestId,
						attachmentIds: confirmedAttachmentIds
					}));
			}

			const failedUploads = uploadResults.filter((result) => !result.success);
			if (failedUploads.length > 0) {
				const message = $("<div>")
					.append($("<p>").text(messages["attachments.upload.failed"]))
					.append($("<ul>").append(
						failedUploads.map((result) => $("<li>").text(result.fileName))
					))
					.append($("<p>").text(messages["attachments.upload.failed.openRequestHint"]));
				const buttons: BootstrapModal.ModalButton[] = [];
				if (onOpenRequestAfterUploadFailed && data.workingTimeRequest) {
					buttons.push({
						text: "requests.openRequest",
						css: "btn-primary",
						action: (modal) => {
							modal.close();
							// Re-fetch the request so the reopened dialog reflects the current server state
							// (e.g. proof status) instead of the pre-upload save snapshot.
							void WorkingTimeRequestUtil.loadWorkingTimeRequestById(data.workingTimeRequest.id)
								.then((freshRequest) => onOpenRequestAfterUploadFailed(freshRequest));
						}
					});
				}
				// No action -> the button just dismisses the modal.
				buttons.push({text: "dialog.close", css: "btn-default"});
				BootstrapModal.error({
					message: message,
					buttons: buttons
				});
			}

		}
		return data;
	}

	private static confirmAttachmentDeletion(fileNames: string): Promise<void> {
		return new Promise((resolve, reject) => {
			BootstrapModal.confirm({
				message: messages.withArguments("attachments.delete.confirmation", fileNames),
				yesAction: (modal) => {
					modal.close();
					resolve();
				},
				noAction: (modal) => {
					modal.close();
					reject(new Error("attachment deletion cancelled"));
				}
			});
		});
	}

	public static isWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel): workingTimeRequest is WorkingTimeRequestResponseModel {
		return workingTimeRequest.type === "MINUTE_BASED";
	}

	public static isWorkingTimeDateSpanRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel): workingTimeRequest is WorkingTimeDateSpanRequestResponseModel {
		return workingTimeRequest.type === "DATE_SPAN";
	}

	public static isWorkingTimeNoticeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel): workingTimeRequest is WorkingTimeNoticeRequestResponseModel {
		return workingTimeRequest.type === "NOTICE";
	}

	public static renderDateRange(workingTimeRequest: AbstractWorkingTimeRequestResponseModel): string {
		if (WorkingTimeRequestUtil.isWorkingTimeDateSpanRequest(workingTimeRequest)) {
			return formatDateRange(
				parseISODate(workingTimeRequest.start),
				parseISODate(workingTimeRequest.end),
				workingTimeRequest.startHalfDay,
				workingTimeRequest.endHalfDay
			);
		} else if (WorkingTimeRequestUtil.isWorkingTimeNoticeRequest(workingTimeRequest)) {
			return formatDateRange(
				parseISODate(workingTimeRequest.start),
				parseISODate(workingTimeRequest.end));
		} else {
			return formatDateTimeRange(
				parseISOZonedDateTime(workingTimeRequest.start),
				parseISOZonedDateTime(workingTimeRequest.end)
			);
		}
	}
}
